import { FC, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  TextInput,
  Textarea,
} from "flowbite-react";
import { HiOutlinePencilAlt, HiPlay, HiPlus, HiTrash } from "react-icons/hi";
import { MdDragIndicator } from "react-icons/md";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  TouchSensor,
  MouseSensor,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import TraditionalWorkoutFileUpload from "./TraditionalWorkoutFileUpload";
import VideoPreviewModal from "./VideoPreviewModal";
import { toast } from "react-toastify";
import {
  createTraditionalExercise,
  updateTraditionalExercise,
} from "../../services/traditionalWorkouts";
import type {
  CreateTraditionalExerciseRequest,
  ProcessingStatus,
  UpdateTraditionalExerciseRequest,
} from "types/traditionalWorkouts";

// Unique key counter for new exercises
let exerciseKeyCounter = 0;

export interface ExerciseFormItem {
  key: number; // Internal key for dnd-kit
  id?: string; // Server ID (undefined for new exercises)
  title: string;
  original_video_storage_key: string | null;
  original_video_url?: string | null;
  processed_video_url?: string | null;
  video_thumbnail_url?: string | null;
  video_processing_status?: ProcessingStatus | null;
  video_duration: number | null; // Raw video duration in seconds (with decimals)
  exercise_duration: number | null; // Computed duration or manual for interval-based
  sets: number;
  reps: string | null; // Can be a single number like "8" or a range like "8-12" (for rep-based exercises)
  set_duration: number | null; // Duration of each set in seconds (for interval-based exercises)
  rest_between_sets: number;
  notes: string | null;
  order: number;
}

const IGNORE_TAGS = ["BUTTON", "INPUT", "TEXTAREA", "SELECT"];

// Block Dnd, if IGNORE_TAGS includes elements tag or element has data-no-dnd attribute
const customHandleEvent = (element: HTMLElement | null) => {
  let cur = element;

  while (cur) {
    if (IGNORE_TAGS.includes(cur.tagName) || cur.dataset["noDnd"]) {
      return false;
    }
    cur = cur.parentElement;
  }

  return true;
};

MouseSensor.activators = [
  {
    eventName: "onMouseDown",
    handler: ({ nativeEvent: event }: { nativeEvent: MouseEvent }) =>
      customHandleEvent(event.target as HTMLElement),
  },
];

TouchSensor.activators = [
  {
    eventName: "onTouchStart",
    handler: ({ nativeEvent: event }: { nativeEvent: TouchEvent }) =>
      customHandleEvent(event.target as HTMLElement),
  },
];

interface SortableExerciseItemProps {
  exercise: ExerciseFormItem;
  onEdit: (exercise: ExerciseFormItem) => void;
  onDelete: (key: number) => void;
}

const SortableExerciseItem: FC<SortableExerciseItemProps> = ({
  exercise,
  onEdit,
  onDelete,
}) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: exercise.key });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    boxShadow: isDragging ? "0 4px 8px rgba(0, 0, 0, 0.2)" : undefined,
    backgroundColor: isDragging ? "#f8fafc" : undefined,
    opacity: isDragging ? 0.9 : 1,
  };

  const formatDuration = (seconds: number | null): string => {
    if (!seconds) return "-";
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${seconds}s`;
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative flex items-center gap-4 rounded-lg border border-gray-300 bg-white px-4 py-3 dark:border-gray-600 dark:bg-gray-800"
    >
      {/* Order badge */}
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-semibold text-blue-800 dark:bg-blue-900 dark:text-blue-300">
        {exercise.order + 1}
      </div>

      {/* Exercise Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="font-semibold text-gray-900 dark:text-white truncate">
            {exercise.title || "Untitled Exercise"}
          </div>
          {exercise.video_processing_status === "PROCESSING" && (
            <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300 shrink-0">
              Processing
            </span>
          )}
          {exercise.video_processing_status === "ERROR" && (
            <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800 dark:bg-red-900 dark:text-red-300 shrink-0">
              Error
            </span>
          )}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 dark:text-gray-400">
          <span>
            <strong>{exercise.sets}</strong> sets
          </span>
          {exercise.reps ? (
            <span>
              <strong>{exercise.reps}</strong> reps
            </span>
          ) : exercise.set_duration ? (
            <span>
              <strong>{exercise.set_duration}s</strong> per set
            </span>
          ) : null}
          <span>
            Rest: <strong>{exercise.rest_between_sets}s</strong>
          </span>
          {exercise.exercise_duration && (
            <span>
              Duration:{" "}
              <strong>{formatDuration(exercise.exercise_duration)}</strong>
            </span>
          )}
        </div>
        {exercise.notes && (
          <div className="mt-1 text-xs text-gray-400 dark:text-gray-500 truncate">
            {exercise.notes}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-1 shrink-0">
        {(exercise.processed_video_url || exercise.original_video_url) && (
          <button
            type="button"
            onClick={() => setIsPreviewOpen(true)}
            className="p-2 text-gray-500 hover:bg-blue-50 hover:text-blue-600 rounded dark:hover:bg-blue-900/20 dark:text-gray-400"
            title="Preview video"
            data-no-dnd="true"
          >
            <HiPlay className="text-lg" />
          </button>
        )}
        <button
          type="button"
          onClick={() => onEdit(exercise)}
          className="p-2 text-gray-500 hover:bg-gray-100 hover:text-blue-600 rounded dark:hover:bg-gray-700 dark:text-gray-400"
          title="Edit exercise"
          data-no-dnd="true"
        >
          <HiOutlinePencilAlt className="text-lg" />
        </button>
        <button
          type="button"
          onClick={() => onDelete(exercise.key)}
          className="p-2 text-gray-500 hover:bg-red-50 hover:text-red-600 rounded dark:hover:bg-red-900/20 dark:text-gray-400"
          title="Delete exercise"
          data-no-dnd="true"
        >
          <HiTrash className="text-lg" />
        </button>
      </div>

      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab p-2 hover:bg-gray-100 rounded dark:hover:bg-gray-700 shrink-0"
        title="Drag to reorder"
      >
        <MdDragIndicator className="text-2xl text-gray-400" />
      </div>

      {/* Video Preview Modal */}
      <VideoPreviewModal
        isOpen={isPreviewOpen}
        toggle={() => setIsPreviewOpen(false)}
        processedVideoUrl={exercise.processed_video_url}
        originalVideoUrl={exercise.original_video_url}
        title={exercise.title}
      />
    </div>
  );
};

// Exercise Modal for Create/Edit
interface ExerciseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (exercise: ExerciseFormItem) => void;
  exercise: ExerciseFormItem | null;
  mode: "create" | "edit";
  isSaving?: boolean;
}

const ExerciseModal: FC<ExerciseModalProps> = ({
  isOpen,
  onClose,
  onSave,
  exercise,
  mode,
  isSaving = false,
}) => {
  const [formData, setFormData] = useState<ExerciseFormItem>(
    exercise || createNewExercise(0),
  );
  const [isVideoUploading, setIsVideoUploading] = useState(false);

  // Store exercise type independently to prevent auto-switching
  const [exerciseType, setExerciseType] = useState<"reps" | "interval">(
    exercise?.set_duration != null ? "interval" : "reps",
  );

  // Reset form when modal opens with new exercise
  useEffect(() => {
    if (isOpen && exercise) {
      setFormData(exercise);
      setExerciseType(exercise.set_duration != null ? "interval" : "reps");
      setIsVideoUploading(false);
    } else if (isOpen && !exercise) {
      const newExercise = createNewExercise(0);
      setFormData(newExercise);
      setExerciseType("reps");
      setIsVideoUploading(false);
    }
  }, [isOpen, exercise]);

  const handleExerciseTypeChange = (type: "reps" | "interval") => {
    setExerciseType(type);
    if (type === "reps") {
      setFormData((prev) => ({
        ...prev,
        set_duration: null,
        reps: prev.reps || "10",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        reps: null,
        set_duration: prev.set_duration || 30,
      }));
    }
  };

  const handleChange = (field: keyof ExerciseFormItem, value: any) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };

      // Auto-recalculate exercise_duration when relevant fields change
      if (
        field === "sets" ||
        field === "reps" ||
        field === "set_duration" ||
        field === "rest_between_sets" ||
        field === "video_duration"
      ) {
        updated.exercise_duration = computeExerciseDuration(
          updated.sets,
          updated.reps,
          updated.set_duration,
          updated.video_duration,
          updated.rest_between_sets,
        );
      }

      return updated;
    });
  };

  const handleSave = () => {
    onSave(formData);
  };

  const isValid =
    (formData.title ?? "").trim() !== "" &&
    formData.sets > 0 &&
    (((formData.reps ?? "") !== "" && (formData.reps ?? "").trim() !== "") ||
      (formData.set_duration != null && formData.set_duration > 0)) &&
    formData.original_video_storage_key !== null &&
    !isVideoUploading;

  return (
    <Modal show={isOpen} onClose={onClose} size="2xl">
      <ModalHeader>
        <strong>
          {mode === "create" ? "Add New Exercise" : "Edit Exercise"}
        </strong>
      </ModalHeader>
      <ModalBody>
        <div className="space-y-4">
          {/* Title */}
          <div>
            <Label htmlFor="exercise-title">Exercise Title</Label>
            <TextInput
              id="exercise-title"
              value={formData.title}
              placeholder="e.g., Push-ups, Squats, Plank"
              onChange={(e) => handleChange("title", e.target.value)}
              className="mt-1"
            />
          </div>

          {/* Sets, Reps/Duration, Rest */}
          <div className="space-y-4">
            {/* Exercise Type Toggle */}
            <div>
              <Label>Exercise Type</Label>
              <div className="mt-2 flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="exerciseType"
                    checked={exerciseType === "reps"}
                    onChange={() => handleExerciseTypeChange("reps")}
                    className="text-blue-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Repetition-based (e.g., 10 reps)
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="exerciseType"
                    checked={exerciseType === "interval"}
                    onChange={() => handleExerciseTypeChange("interval")}
                    className="text-blue-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Interval-based (e.g., 30 seconds)
                  </span>
                </label>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="exercise-sets">Sets</Label>
                <TextInput
                  id="exercise-sets"
                  type="number"
                  min={1}
                  value={formData.sets || ""}
                  placeholder="3"
                  onChange={(e) =>
                    handleChange(
                      "sets",
                      e.target.value === "" ? 0 : parseInt(e.target.value) || 0,
                    )
                  }
                  className="mt-1"
                />
              </div>

              {exerciseType === "reps" ? (
                <div>
                  <Label htmlFor="exercise-reps">Reps</Label>
                  <TextInput
                    id="exercise-reps"
                    type="text"
                    value={formData.reps || ""}
                    placeholder="10 or 8-12"
                    onChange={(e) =>
                      handleChange("reps", e.target.value || null)
                    }
                    className="mt-1"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Enter a number (e.g., "10") or range (e.g., "8-12")
                  </p>
                </div>
              ) : (
                <div>
                  <Label htmlFor="exercise-set-duration">
                    Set Duration (seconds)
                  </Label>
                  <TextInput
                    id="exercise-set-duration"
                    type="number"
                    min={1}
                    value={formData.set_duration || ""}
                    placeholder="30"
                    onChange={(e) =>
                      handleChange(
                        "set_duration",
                        e.target.value === ""
                          ? null
                          : parseInt(e.target.value) || null,
                      )
                    }
                    className="mt-1"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Duration of each set in seconds
                  </p>
                </div>
              )}

              <div>
                <Label htmlFor="exercise-rest">Rest (seconds)</Label>
                <TextInput
                  id="exercise-rest"
                  type="number"
                  min={0}
                  value={formData.rest_between_sets || ""}
                  placeholder="30"
                  onChange={(e) =>
                    handleChange(
                      "rest_between_sets",
                      e.target.value === "" ? 0 : parseInt(e.target.value) || 0,
                    )
                  }
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="exercise-duration">
                  Exercise Duration (seconds)
                </Label>
                <TextInput
                  id="exercise-duration"
                  type="number"
                  min={0}
                  value={formData.exercise_duration || ""}
                  placeholder="Auto-calculated"
                  readOnly
                  disabled
                  className="mt-1 bg-gray-100 dark:bg-gray-700"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {exerciseType === "reps"
                    ? "Auto-calculated from sets, reps, video duration & rest"
                    : "Auto-calculated from sets, set duration & rest"}
                </p>
              </div>
            </div>
          </div>

          {/* Exercise Video */}
          <div>
            <Label>Exercise Video</Label>
            <TraditionalWorkoutFileUpload
              fileType="video"
              acceptedTypes={["video/mp4", "video/quicktime", "video/webm"]}
              maxSize={1024 * 1024 * 1024} // 1GB
              defaultPreview={
                formData.video_thumbnail_url ||
                formData.original_video_storage_key
                  ? {
                      url: formData.video_thumbnail_url ?? undefined,
                      name: formData.original_video_storage_key ?? undefined,
                    }
                  : undefined
              }
              onUploadStart={() => setIsVideoUploading(true)}
              onUploadComplete={(_, storageKey) => {
                handleChange("original_video_storage_key", storageKey);
                // Clear the thumbnail since it's a new upload
                handleChange("video_thumbnail_url", null);
                setIsVideoUploading(false);
              }}
              onUploadError={() => setIsVideoUploading(false)}
              onFileSelected={(file) => {
                // Get video duration from file (with decimals) and compute exercise duration
                const video = document.createElement("video");
                video.preload = "metadata";
                video.onloadedmetadata = () => {
                  URL.revokeObjectURL(video.src);
                  // Store raw video duration with decimals
                  const videoDuration = video.duration;
                  handleChange("video_duration", videoDuration);
                  // Compute exercise duration using current sets, reps/set_duration and rest values
                  const computedDuration = computeExerciseDuration(
                    formData.sets,
                    formData.reps,
                    formData.set_duration,
                    videoDuration,
                    formData.rest_between_sets,
                  );
                  handleChange("exercise_duration", computedDuration);
                };
                video.src = URL.createObjectURL(file);
              }}
            />
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              Required. Exercise duration is computed based on exercise type.
            </p>
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="exercise-notes">Notes</Label>
            <Textarea
              id="exercise-notes"
              value={formData.notes || ""}
              placeholder="Additional instructions or tips..."
              rows={3}
              onChange={(e) => handleChange("notes", e.target.value || null)}
              className="mt-1"
            />
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          color="primary"
          onClick={handleSave}
          disabled={!isValid || isSaving || isVideoUploading}
        >
          {isVideoUploading
            ? "Uploading video..."
            : isSaving
              ? mode === "create"
                ? "Adding..."
                : "Saving..."
              : mode === "create"
                ? "Add Exercise"
                : "Save Changes"}
        </Button>
        <Button color="light" onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

interface ExercisesSectionProps {
  exercises: ExerciseFormItem[];
  onChange: (exercises: ExerciseFormItem[]) => void;
}

export const createNewExercise = (order: number): ExerciseFormItem => ({
  key: ++exerciseKeyCounter,
  title: "",
  original_video_storage_key: null,
  original_video_url: null,
  processed_video_url: null,
  video_thumbnail_url: null,
  video_duration: null,
  exercise_duration: null,
  sets: 3,
  reps: "10",
  set_duration: null,
  rest_between_sets: 30,
  notes: null,
  order,
});

// Compute exercise duration based on exercise type
// For rep-based: sets × lower_reps × video_duration + (sets - 1) × rest_between_sets
// For interval-based: sets × set_duration + (sets - 1) × rest_between_sets
export const computeExerciseDuration = (
  sets: number,
  reps: string | null,
  setDuration: number | null,
  videoDuration: number | null,
  restBetweenSets: number,
): number | null => {
  const totalRestTime = Math.max(0, sets - 1) * restBetweenSets;

  // Interval-based exercise
  if (setDuration !== null && setDuration > 0) {
    return Math.round(sets * setDuration + totalRestTime);
  }

  // Rep-based exercise
  if (reps !== null && videoDuration !== null && videoDuration > 0) {
    // Parse the lower bound from reps (e.g., "8-12" -> 8, "10" -> 10)
    const repsParts = reps.split("-");
    const lowerReps = parseInt(repsParts[0] ?? "0") || 0;
    if (lowerReps > 0) {
      const totalRepTime = sets * lowerReps * videoDuration;
      return Math.round(totalRepTime + totalRestTime);
    }
  }

  return null;
};

export const initializeExerciseKey = (
  exercise: Omit<ExerciseFormItem, "key">,
): ExerciseFormItem => ({
  ...exercise,
  key: ++exerciseKeyCounter,
});

const ExercisesSection: FC<ExercisesSectionProps> = ({
  exercises,
  onChange,
}) => {
  const keyCounterRef = useRef(exerciseKeyCounter);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingExercise, setEditingExercise] =
    useState<ExerciseFormItem | null>(null);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [isSaving, setIsSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = exercises.findIndex((ex) => ex.key === active.id);
    const newIndex = exercises.findIndex((ex) => ex.key === over.id);

    const newExercises = arrayMove(exercises, oldIndex, newIndex).map(
      (ex, index) => ({
        ...ex,
        order: index,
      }),
    );

    onChange(newExercises);
  };

  const handleOpenCreateModal = () => {
    setEditingExercise(null);
    setModalMode("create");
    setModalOpen(true);
  };

  const handleOpenEditModal = (exercise: ExerciseFormItem) => {
    setEditingExercise(exercise);
    setModalMode("edit");
    setModalOpen(true);
  };

  const handleSaveExercise = async (exercise: ExerciseFormItem) => {
    setIsSaving(true);
    try {
      if (modalMode === "create") {
        // Create new exercise via API
        const exercisePayload: CreateTraditionalExerciseRequest = {
          title: exercise.title,
          sets: exercise.sets,
          rest_between_sets: exercise.rest_between_sets,
        };

        // Add reps or set_duration based on exercise type
        if (exercise.reps !== null) {
          exercisePayload.reps = exercise.reps;
        }
        if (exercise.set_duration !== null) {
          exercisePayload.set_duration = exercise.set_duration;
        }

        if (exercise.original_video_storage_key) {
          exercisePayload.original_video_storage_key =
            exercise.original_video_storage_key;
        }
        if (exercise.video_duration !== null) {
          exercisePayload.video_duration = exercise.video_duration;
        }
        if (exercise.exercise_duration) {
          exercisePayload.exercise_duration = exercise.exercise_duration;
        }
        if (exercise.notes) {
          exercisePayload.notes = exercise.notes;
        }

        const createdExercise =
          await createTraditionalExercise(exercisePayload);
        if (createdExercise.results) {
          const newExercise: ExerciseFormItem = {
            key: ++exerciseKeyCounter,
            id: createdExercise.results.id,
            title: createdExercise.results.title,
            original_video_storage_key:
              createdExercise.results.original_video_storage_key,
            original_video_url: createdExercise.results.original_video_url,
            processed_video_url: createdExercise.results.processed_video_url,
            video_thumbnail_url: createdExercise.results.video_thumbnail_url,
            video_processing_status:
              createdExercise.results.video_processing_status,
            video_duration: exercise.video_duration,
            exercise_duration: createdExercise.results.exercise_duration,
            sets: createdExercise.results.sets,
            reps: createdExercise.results.reps,
            set_duration: createdExercise.results.set_duration,
            rest_between_sets: createdExercise.results.rest_between_sets,
            notes: createdExercise.results.notes,
            order: exercises.length,
          };
          keyCounterRef.current = exerciseKeyCounter;
          onChange([...exercises, newExercise]);
          toast.success(`Exercise "${exercise.title}" created`);
        }
      } else {
        // Update existing exercise via API
        if (exercise.id && editingExercise) {
          // Only send properties that were changed
          const updatePayload: UpdateTraditionalExerciseRequest = {};

          if (exercise.title !== editingExercise.title) {
            updatePayload.title = exercise.title;
          }
          if (
            exercise.original_video_storage_key !==
            editingExercise.original_video_storage_key
          ) {
            updatePayload.original_video_storage_key =
              exercise.original_video_storage_key ?? undefined;
          }
          if (exercise.video_duration !== editingExercise.video_duration) {
            updatePayload.video_duration =
              exercise.video_duration !== null
                ? exercise.video_duration
                : undefined;
          }
          if (
            exercise.exercise_duration !== editingExercise.exercise_duration
          ) {
            updatePayload.exercise_duration =
              exercise.exercise_duration !== null
                ? Number(exercise.exercise_duration)
                : undefined;
          }
          if (exercise.sets !== editingExercise.sets) {
            updatePayload.sets = exercise.sets;
          }
          if (exercise.reps !== editingExercise.reps) {
            // Send null explicitly to clear the field when switching to interval type
            updatePayload.reps = exercise.reps;
          }
          if (exercise.set_duration !== editingExercise.set_duration) {
            // Send null explicitly to clear the field when switching to reps type
            updatePayload.set_duration = exercise.set_duration;
          }
          if (
            exercise.rest_between_sets !== editingExercise.rest_between_sets
          ) {
            updatePayload.rest_between_sets = exercise.rest_between_sets;
          }
          if (exercise.notes !== editingExercise.notes) {
            updatePayload.notes = exercise.notes ?? undefined;
          }

          // Only make API call if there are changes
          if (Object.keys(updatePayload).length > 0) {
            const updatedExerciseResponse = await updateTraditionalExercise(
              exercise.id,
              updatePayload,
            );

            // Update local state with the fresh data from API response
            if (updatedExerciseResponse.results) {
              const updatedExercise: ExerciseFormItem = {
                ...exercise,
                title: updatedExerciseResponse.results.title,
                original_video_storage_key:
                  updatedExerciseResponse.results.original_video_storage_key,
                original_video_url:
                  updatedExerciseResponse.results.original_video_url,
                processed_video_url:
                  updatedExerciseResponse.results.processed_video_url,
                video_thumbnail_url:
                  updatedExerciseResponse.results.video_thumbnail_url,
                video_processing_status:
                  updatedExerciseResponse.results.video_processing_status,
                exercise_duration:
                  updatedExerciseResponse.results.exercise_duration,
                sets: updatedExerciseResponse.results.sets,
                reps: updatedExerciseResponse.results.reps,
                set_duration: updatedExerciseResponse.results.set_duration,
                rest_between_sets:
                  updatedExerciseResponse.results.rest_between_sets,
                notes: updatedExerciseResponse.results.notes,
              };

              const updatedExercises = exercises.map((ex) =>
                ex.key === exercise.key ? updatedExercise : ex,
              );
              onChange(updatedExercises);
            }

            toast.success(`Exercise "${exercise.title}" updated`);
          } else {
            toast.info("No changes to save");
          }
        }
      }
      setModalOpen(false);
    } catch (error) {
      console.error("Error saving exercise:", error);
      toast.error(
        `Failed to ${modalMode === "create" ? "create" : "update"} exercise`,
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteExercise = (key: number) => {
    const updatedExercises = exercises
      .filter((ex) => ex.key !== key)
      .map((ex, index) => ({ ...ex, order: index }));
    onChange(updatedExercises);
  };

  const exerciseIds = useMemo(() => exercises.map((ex) => ex.key), [exercises]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-base font-semibold">Exercises</Label>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {exercises.length} exercise{exercises.length !== 1 ? "s" : ""}
        </span>
      </div>

      {exercises.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 p-8 dark:border-gray-600">
          <p className="mb-4 text-gray-500 dark:text-gray-400">
            No exercises added yet. Add your first exercise to get started.
          </p>
          <Button color="primary" onClick={handleOpenCreateModal}>
            <HiPlus className="mr-2 text-lg" />
            Add First Exercise
          </Button>
        </div>
      ) : (
        <>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={exerciseIds}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {exercises.map((exercise) => (
                  <SortableExerciseItem
                    key={exercise.key}
                    exercise={exercise}
                    onEdit={handleOpenEditModal}
                    onDelete={handleDeleteExercise}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          <Button
            color="light"
            onClick={handleOpenCreateModal}
            className="w-full"
          >
            <HiPlus className="mr-2 text-lg" />
            Add Exercise
          </Button>
        </>
      )}

      {/* Exercise Modal */}
      <ExerciseModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSaveExercise}
        exercise={editingExercise}
        mode={modalMode}
        isSaving={isSaving}
      />
    </div>
  );
};

export default ExercisesSection;
